import React, { useMemo } from 'react';
import { EditFormFields, EditModalProps } from './types';
import { Button } from '@vtbhome/juicyfront';
import { Body, Footer } from './modal-edit-user.styles';
import { Typography } from '../../../../../shared/ui/typography/typography';
import { Field, Form } from 'react-final-form';
import { validate } from '../../lib/form-validate';
import { EditFormKeys } from '../../config/form-constants';
import { Select } from '../../../../../shared/ui/select/select';
import { UserInfo } from '../user-info/user-info';
import { getUserStatus } from '@shared/lib/utils/get-user-status/get-user-status';
import { useUserEditMutation } from '../../api/user-edit-api';
import { useUserRolesListQuery } from '@entities/user/api/user-management-api';
import { userRoleId } from '../../lib/user-role-id';
import { UserRoles } from '@shared/model/user';
import { selectValue } from '@shared/lib/utils/select-value/select-value';
import { CACHE_KEY_USER_EDIT } from '../../config/api-constants';
import { USER_ROLE_OPTIONS } from '@entities/user/config/ui-constants';
import { Modal } from '@shared/ui/modal/modal';
import { useResetModalForm } from '@shared/lib/hooks/use-reset-modal-form/use-reset-modal-form';
import { userName } from '@shared/lib/utils/user-name/user-name';

export const ModalEditUser: React.FC<EditModalProps> = ({ isShow, user, onClose, onAccept }) => {
    const roles = useUserRolesListQuery({ take: null, skip: null, search: '' });
    const [editUser, editUserResult] = useUserEditMutation({ fixedCacheKey: CACHE_KEY_USER_EDIT });
    const formRef = useResetModalForm<EditFormFields>(isShow);

    const userCurrentRoleOption = useMemo(() => {
        return USER_ROLE_OPTIONS.find((option) => option.value === user.role.code)?.value;
    }, [user]);

    const handleSubmit = async (fields: Record<EditFormKeys, string>) => {
        const result = await editUser({
            id: user.id,
            roleId: userRoleId({ items: roles.data?.items, role: fields.role as UserRoles }),
        });
        if (!result.error) {
            onAccept();
        }
    };

    return (
        <Form
            subscription={{}}
            onSubmit={handleSubmit}
            validate={validate}
            render={(form) => {
                formRef.current = form;
                return (
                    <Modal
                        isShow={isShow}
                        size='l'
                        showLine={false}
                        header={<Typography size='h5'>Редактирование пользователя</Typography>}
                        footer={
                            <Footer>
                                <Button disabled={editUserResult.isLoading} buttonType='light' onClick={onClose}>
                                    Отменить
                                </Button>
                                <Button
                                    buttonType='primary'
                                    preloader={editUserResult.isLoading}
                                    disabled={editUserResult.isLoading}
                                    onClick={form.handleSubmit}>
                                    Подтвердить
                                </Button>
                            </Footer>
                        }
                        onClose={onClose}>
                        <Body>
                            <UserInfo
                                avatarUrl={user.photoUrl}
                                phoneNumber={user.phoneNumber}
                                email={user.email}
                                departmentName={user.department?.name || ''}
                                name={
                                    userName({
                                        firstName: user.firstName,
                                        lastName: user.lastName,
                                        middleName: user.middleName,
                                    }).full
                                }
                                organizationPost={user.jobTitle}
                                status={getUserStatus(user)}>
                                <Field
                                    name={EditFormKeys.Role}
                                    initialValue={userCurrentRoleOption}
                                    render={({ input, meta }) => (
                                        <Select
                                            values={selectValue({ options: USER_ROLE_OPTIONS, value: input.value })}
                                            options={USER_ROLE_OPTIONS}
                                            label='Вы можете изменить роль данного пользователя. '
                                            placeholder='Выберите роль'
                                            error={meta.touched && meta.error}
                                            onChange={(option) => {
                                                input.onChange({ target: { value: option[0].value } });
                                            }}
                                        />
                                    )}
                                />
                            </UserInfo>
                        </Body>
                    </Modal>
                );
            }}
        />
    );
};
